<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownBody from 'next/dropdown-menu/base/DropdownBody.vue';
import Icon from 'next/icon/Icon.vue';

defineProps({
  hasContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['executeKimiAction']);
const { t } = useI18n();

const conversationActions = computed(() => [
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.DRAFT_REPLY'),
    description: t('ACTIVE_GAMES_HUB.AI.COMPOSER.DRAFT_REPLY_DESCRIPTION'),
    key: 'reply_suggestion',
    icon: 'i-lucide-message-circle-reply',
  },
]);

const draftActions = computed(() => [
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.IMPROVE_DRAFT'),
    key: 'improve',
    icon: 'i-lucide-wand-sparkles',
  },
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.MAKE_WARMER'),
    key: 'make_friendly',
    icon: 'i-lucide-heart-handshake',
  },
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.PROFESSIONAL_TONE'),
    key: 'professional',
    icon: 'i-lucide-briefcase-business',
  },
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.SHORTEN_REPLY'),
    key: 'shorten',
    icon: 'i-lucide-align-justify',
  },
  {
    label: t('ACTIVE_GAMES_HUB.AI.COMPOSER.FIX_WRITING'),
    key: 'fix_spelling_grammar',
    icon: 'i-lucide-spell-check-2',
  },
]);

const runAction = action => {
  emit('executeKimiAction', action.key);
};
</script>

<template>
  <DropdownBody class="z-50 min-w-72 overflow-hidden [&>ul]:gap-0 [&>ul]:p-0">
    <div class="flex items-center gap-3 px-4 pb-3 pt-4">
      <span
        class="flex size-9 shrink-0 items-center justify-center rounded-xl bg-n-violet-3 text-sm font-black text-n-violet-11 ring-1 ring-n-violet-5"
        aria-hidden="true"
      >
        {{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.MARK') }}
      </span>
      <div class="min-w-0">
        <p class="m-0 text-sm font-bold text-n-slate-12">
          {{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.TITLE') }}
        </p>
        <p class="m-0 mt-0.5 text-xs leading-4 text-n-slate-10">
          {{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.SUBTITLE') }}
        </p>
      </div>
    </div>

    <div class="h-px w-full bg-n-weak" />

    <div class="px-2 py-2">
      <p
        class="m-0 px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-widest text-n-slate-9"
      >
        {{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.CONVERSATION') }}
      </p>
      <Button
        v-for="action in conversationActions"
        :key="action.key"
        :label="action.label"
        :icon="action.icon"
        slate
        link
        sm
        class="w-full !justify-start text-xs font-medium text-n-slate-12 hover:!no-underline"
        @click="runAction(action)"
      />
      <p class="m-0 px-9 pb-1 text-[11px] leading-4 text-n-slate-9">
        {{ conversationActions[0].description }}
      </p>
    </div>

    <template v-if="hasContent">
      <div class="mx-2 h-px bg-n-weak" />
      <div class="px-2 py-2">
        <p
          class="m-0 px-2 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-widest text-n-slate-9"
        >
          {{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.YOUR_DRAFT') }}
        </p>
        <Button
          v-for="action in draftActions"
          :key="action.key"
          :label="action.label"
          :icon="action.icon"
          slate
          link
          sm
          class="w-full !justify-start text-xs font-normal text-n-slate-12 hover:!no-underline"
          @click="runAction(action)"
        />
      </div>
    </template>

    <div
      class="flex items-center gap-2 border-t border-n-weak bg-n-alpha-1 px-4 py-3 text-[11px] leading-4 text-n-slate-10"
    >
      <Icon icon="i-lucide-shield-check" class="size-4 shrink-0" />
      <span>{{ t('ACTIVE_GAMES_HUB.AI.COMPOSER.HUMAN_SEND_GUARD') }}</span>
    </div>
  </DropdownBody>
</template>
